import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import API from "../api/api";
import { BACKEND_URL } from "../api/api";
import SignaturePad from "../components/SignaturePad";
import {
  confirmCheckout,
  confirmPublicTrailerCheckout,
  getCheckoutData,
  getPublicTrailerCheckout,
  verifyPublicTrailerCheckout
} from "../api/checkoutApi";

const imageBase = BACKEND_URL;

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("nl-BE");
};

const parseList = (value) => {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export default function CheckoutPage() {
  const { reservationId, qrToken } = useParams();
  const routeKey = qrToken || reservationId;
  const isTrailerQrFlow = Boolean(qrToken);
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState({});
  const [signatureDataUrl, setSignatureDataUrl] = useState("");
  const [signatureError, setSignatureError] = useState("");
  const [confirmed, setConfirmed] = useState(null);
  const [verifyForm, setVerifyForm] = useState({ reservationCode: "", phoneOrEmail: "" });
  const [verifiedData, setVerifiedData] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: isTrailerQrFlow ? ["publicTrailerCheckout", qrToken] : ["checkout", reservationId],
    queryFn: () => isTrailerQrFlow ? getPublicTrailerCheckout(qrToken) : getCheckoutData(reservationId)
  });

  const checkoutData = isTrailerQrFlow ? verifiedData : data;
  const checklist = useMemo(() => checkoutData?.requiredPhotoChecklist || [], [checkoutData]);
  const existingDamageCases = checkoutData?.existingDamageCases || [];
  const missingItems = useMemo(() => (
    checklist.filter((item) => !photos[item.type]?.imageUrl)
  ), [checklist, photos]);

  const uploadMutation = useMutation({
    mutationFn: async ({ item, file }) => {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("type", item.type);
      const res = await API.post(`/upload/${checkoutData?.reservation?.id || routeKey}`, formData);
      return { item, imageUrl: res.data.filePath };
    },
    onSuccess: ({ item, imageUrl }) => {
      setPhotos((current) => ({
        ...current,
        [item.type]: {
          type: item.type,
          label: item.label,
          imageUrl
        }
      }));
      toast.success(`${item.label} toegevoegd`);
    },
    onError: () => toast.error("Foto uploaden mislukt")
  });

  const confirmMutation = useMutation({
    mutationFn: () => {
      const payload = {
        checkoutSessionToken: verifiedData?.checkoutSessionToken,
        notes,
        photos: Object.values(photos),
        signatureDataUrl
      };

      return isTrailerQrFlow
        ? confirmPublicTrailerCheckout(qrToken, payload)
        : confirmCheckout(reservationId, payload);
    },
    onSuccess: (result) => {
      setConfirmed(result);
      toast.success("Check-out bevestigd");
    },
    onError: (error) => toast.error(error.response?.data?.error || "Check-out bevestigen mislukt")
  });

  const verifyMutation = useMutation({
    mutationFn: () => verifyPublicTrailerCheckout(qrToken, verifyForm),
    onSuccess: (result) => {
      setVerifiedData(result);
      toast.success("Reservatie geverifieerd");
    },
    onError: (error) => toast.error(error.response?.data?.error || "Verificatie mislukt")
  });

  const submitCheckout = () => {
    if (!signatureDataUrl) {
      setSignatureError("Handtekening is verplicht.");
      toast.error("Plaats eerst een handtekening.");
      return;
    }
    confirmMutation.mutate();
  };

  if (isLoading) {
    return <main className="checkout-mobile"><div className="loading-state">Check-out laden...</div></main>;
  }

  if (isError || !data) {
    return (
      <main className="checkout-mobile">
        <div className="panel panel-pad">
          <h1 className="page-title">Check-out niet gevonden</h1>
          <p className="page-subtitle">Controleer de link of vraag een medewerker om hulp.</p>
          <Link className="button secondary" to="/">Terug naar homepagina</Link>
        </div>
      </main>
    );
  }

  if (isTrailerQrFlow && !verifiedData) {
    return (
      <main className="checkout-mobile">
        <section className="checkout-header">
          <p className="sidebar-brand-title">DR-Renting</p>
          <h1 className="page-title">Trailer check-out</h1>
          <p className="page-subtitle">Verifieer je reservatie voordat de check-outgegevens zichtbaar worden.</p>
        </section>

        <section className="panel panel-pad checkout-verify-card">
          <h2 className="detail-title">{data.trailer?.name || "Trailer"}</h2>
          <p className="page-subtitle">De QR-code identificeert alleen de trailer. Vul je reservatiecode en telefoonnummer of e-mailadres in.</p>
          <div className="form-grid">
            <label className="field">
              <span className="field-label">Reservatiecode</span>
              <input
                className="input"
                value={verifyForm.reservationCode}
                onChange={(event) => setVerifyForm({ ...verifyForm, reservationCode: event.target.value })}
                autoComplete="one-time-code"
              />
            </label>
            <label className="field">
              <span className="field-label">Telefoonnummer of e-mailadres</span>
              <input
                className="input"
                value={verifyForm.phoneOrEmail}
                onChange={(event) => setVerifyForm({ ...verifyForm, phoneOrEmail: event.target.value })}
                autoComplete="email"
              />
            </label>
          </div>
          <button
            className="button full-width-button checkout-submit-button"
            disabled={!verifyForm.reservationCode || !verifyForm.phoneOrEmail || verifyMutation.isPending}
            onClick={() => verifyMutation.mutate()}
          >
            {verifyMutation.isPending ? "Controleren..." : "Reservatie verifiëren"}
          </button>
        </section>
      </main>
    );
  }

  if (confirmed) {
    return (
      <main className="checkout-mobile">
        <div className="panel panel-pad checkout-success">
          <h1 className="page-title">Check-out bevestigd</h1>
          <p className="page-subtitle">De foto's en opmerkingen zijn opgeslagen. De reservatie staat nu op uitgecheckt.</p>
          <div className="detail-grid">
            <div className="detail-item"><div className="detail-label">Reservatie</div><div className="detail-value">#{confirmed.reservation.id}</div></div>
            <div className="detail-item"><div className="detail-label">Trailer</div><div className="detail-value">{confirmed.trailer.name}</div></div>
          </div>
        </div>
      </main>
    );
  }

  const { reservation, trailer, customer, requestedOptions } = checkoutData;

  return (
    <main className="checkout-mobile">
      <section className="checkout-header">
        <p className="sidebar-brand-title">DR-Renting</p>
        <h1 className="page-title">Trailer check-out</h1>
        <p className="page-subtitle">Controleer de trailer bij ophaling en voeg de verplichte foto's toe.</p>
      </section>

      <section className="panel panel-pad">
        <h2 className="detail-title">Reservatie</h2>
        <div className="detail-grid">
          <div className="detail-item"><div className="detail-label">Reservatie</div><div className="detail-value">#{reservation.id}</div></div>
          <div className="detail-item"><div className="detail-label">Periode</div><div className="detail-value">{formatDate(reservation.startDate)} tot {formatDate(reservation.endDate)}</div></div>
          <div className="detail-item"><div className="detail-label">Klant</div><div className="detail-value">{`${customer?.firstName || ""} ${customer?.lastName || ""}`.trim() || reservation.name}</div></div>
          <div className="detail-item"><div className="detail-label">Status</div><div className="detail-value">{reservation.status}</div></div>
        </div>
      </section>

      <section className="panel panel-pad">
        <h2 className="detail-title">Trailer</h2>
        <div className="detail-grid">
          <div className="detail-item"><div className="detail-label">Naam</div><div className="detail-value">{trailer.name}</div></div>
          <div className="detail-item"><div className="detail-label">Trailerstatus</div><div className="detail-value">{trailer.status || "AVAILABLE"}</div></div>
          <div className="detail-item"><div className="detail-label">MTM</div><div className="detail-value">{trailer.mtm} kg</div></div>
          <div className="detail-item"><div className="detail-label">Rijbewijs</div><div className="detail-value">{trailer.licenseType || "-"}</div></div>
        </div>
      </section>

      <section className="panel panel-pad">
        <h2 className="detail-title">Aangevraagde opties</h2>
        {requestedOptions.length ? (
          <div className="status-line">
            {requestedOptions.map((option) => <span key={option} className="badge info">{option}</span>)}
          </div>
        ) : (
          <div className="empty-state">Geen opties opgegeven.</div>
        )}
      </section>

      <section className="panel panel-pad">
        <h2 className="detail-title">Reeds gekende schade</h2>
        {existingDamageCases.length === 0 && <div className="empty-state">Geen openstaande schadegevallen gekend.</div>}
        {existingDamageCases.map((damageCase) => {
          const damagePhotos = parseList(damageCase.photos);
          return (
            <div key={damageCase.id} className="checkout-damage">
              {damageCase.position && <span className="badge danger">{damageCase.position}</span>}
              <p className="page-subtitle">{damageCase.description || "Geen omschrijving"}</p>
              <p className="row-meta">{formatDate(damageCase.createdAt)}</p>
              {damagePhotos.length > 0 && (
                <div className="photo-grid">
                  {damagePhotos.map((photo) => (
                    <img key={photo} className="photo-thumb" src={`${imageBase}${photo}`} alt="Gekende schade" />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </section>

      <section className="panel panel-pad">
        <h2 className="detail-title">Foto-checklist</h2>
        <div className="checkout-checklist">
          {checklist.map((item) => {
            const photo = photos[item.type];
            const inputId = `checkout-photo-${item.type}`;

            return (
              <div key={item.type} className={`checkout-photo-item${photo ? " complete" : ""}`}>
                <div className="checkout-photo-copy">
                  <span className="detail-label">{item.option ? "Optie/accessoire" : "Trailerpositie"}</span>
                  <strong>{item.label}</strong>
                  <span className={`checkout-photo-status${photo ? " complete" : ""}`}>
                    {photo ? "Foto toegevoegd" : "Nog niet toegevoegd"}
                  </span>
                </div>

                {photo ? (
                  <img className="checkout-photo-preview" src={`${imageBase}${photo.imageUrl}`} alt={item.label} />
                ) : (
                  <div className="checkout-photo-placeholder">Geen foto</div>
                )}

                <input
                  id={inputId}
                  className="checkout-file-input"
                  type="file"
                  accept="image/jpeg,image/png"
                  capture="environment"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) uploadMutation.mutate({ item, file });
                    event.target.value = "";
                  }}
                />
                <label className="button checkout-camera-button" htmlFor={inputId}>
                  {photo ? "Foto vervangen" : "Foto nemen"}
                </label>
                <p className="row-meta checkout-photo-hint">Camera op gsm, galerij als fallback.</p>
              </div>
            );
          })}
        </div>
        {missingItems.length > 0 && (
          <p className="error-text">Nog ontbrekend: {missingItems.map((item) => item.label).join(", ")}</p>
        )}
      </section>

      <section className="panel panel-pad">
        <label className="field">
          <span className="field-label">Opmerkingen</span>
          <textarea className="textarea" value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>
      </section>

      <section className="panel panel-pad">
        <SignaturePad
          label="Digitale handtekening"
          confirmationText="Ik bevestig dat ik de aanhangwagen in bovenstaande staat terugbezorg."
          value={signatureDataUrl}
          onChange={(nextSignature) => {
            setSignatureDataUrl(nextSignature);
            if (nextSignature) setSignatureError("");
          }}
          error={signatureError}
        />
      </section>

      <div className="checkout-submit-bar">
        <div>
          <strong>{missingItems.length === 0 && signatureDataUrl ? "Alles klaar" : "Controle ontbreekt"}</strong>
          <span>{uploadMutation.isPending ? "Foto uploaden..." : "Controleer foto's en handtekening voor bevestiging."}</span>
        </div>
        <button
          className="button checkout-submit-button"
          disabled={missingItems.length > 0 || confirmMutation.isPending || uploadMutation.isPending}
          onClick={submitCheckout}
        >
          {confirmMutation.isPending ? "Bevestigen..." : "Check-out bevestigen"}
        </button>
      </div>
    </main>
  );
}
